import {
    Box,
    DatePicker,
    Text,
    ThemeColorVariable,
    ThemeSpaceVariable,
} from "@caspeco/casper-ui-library";
import {
    Days,
    getDayStatesMonthKey,
    getDayStatesQueryKey,
    getNonAvailableDatesForExperience,
    getNonAvailableDatesForGuests,
    getNonAvailableDatesForSection,
    useDayStates,
} from "api/api-hooks/use-day-states";
import { useExternalBookingSettings } from "api/api-hooks/use-external-booking-settings";
import { getDatePickerLocale, getLocaleFromLang } from "helpers/language-helper";
import { useFirstAvailableDay, useHasAvailableDaysInMonth } from "hooks/use-available-days";
import { useBookingActionsContext, useBookingStateContext } from "hooks/use-booking-state";
import { useStepTracking } from "logger/use-step-tracking";
import { usePageTracking } from "logger/use-page-tracking";
import { PageType } from "logger/page-tracking";
import i18n from "i18next";
import { TrackingStepName } from "logger/user-tracking";
import moment from "moment";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { isDateWithinBookingWindow, shouldDisableDate } from "utils/datepicker-utils";
import SelectDateSkeleton from "../select-date-skeleton";
import DatePickerLegend from "./date-picker-legend";
import FirstAvailableDay from "./first-available-day";

export const SelectDate = ({
    onChange,
    date,
    bookingRequestMode,
    sectionId,
    experienceId,
    guestsOverride,
    requestOnly,
    informationOnly,
}: {
    onChange?: (date?: Date) => void;
    date?: Date;
    bookingRequestMode?: boolean;
    sectionId?: number;
    experienceId?: number;
    guestsOverride?: number;
    /**
     * The experience takes requests only. Its days carry no bookable seats, so every open day
     * would otherwise be dotted as fully booked — a marker identical on every date, saying
     * nothing. The caller explains the request-only nature above the calendar instead (BOOK-3911).
     */
    requestOnly?: boolean;
    /**
     * The experience is information only: nothing on it can be booked, but its section's other
     * rules would still dot its days as fully booked.
     */
    informationOnly?: boolean;
}) => {
    const { t } = useTranslation();
    const [datePickerLocale, setDatePickerLocale] = useState(
        getDatePickerLocale(getLocaleFromLang(i18n.language))
    );
    const externalBookingSettings = useExternalBookingSettings().data;
    const state = useBookingStateContext();
    const { guests, selectedMonth } = state;
    const { setState } = useBookingActionsContext();
    const queryClient = useQueryClient();
    const dayState = useDayStates(selectedMonth ?? new Date(), bookingRequestMode);

    // These drive UI that only renders outside booking-request mode, and their
    // underlying query is the heavier standard-mode fetch — skip it for requests.
    const firstAvailableDay = useFirstAvailableDay(
        selectedMonth ?? new Date(),
        !bookingRequestMode
    );
    const hasAvailableDaysForMonth = useHasAvailableDaysInMonth(
        selectedMonth ?? new Date(),
        !bookingRequestMode
    );

    // Track date selection step
    useStepTracking({
        stepName: TrackingStepName.DateStep,
        autoTrack: true,
    });
    const guestCount = guestsOverride ?? guests ?? 1;
    usePageTracking({
        pageName: "SelectDate",
        pageType: PageType.WizardStep,
        metadata: {
            bookingRequestMode: !!bookingRequestMode,
            guests: guestCount,
            hasAvailableDays: hasAvailableDaysForMonth,
        },
    });

    const setSelectedMonth = (date: Date) => {
        setState?.((prev) => ({
            ...prev,
            selectedMonth: date,
        }));
    };

    const goToNextAvailableDay = () => {
        if (firstAvailableDay) {
            setSelectedMonth(firstAvailableDay);
        }
    };

    /**
     * Wraps the onChange callback to validate the selected date against day state data.
     *
     * If the date's month data is not yet in the cache (e.g. the user is viewing
     * a future month and clicks the built-in "Today" button), we navigate to that
     * month so the data is fetched and the user can see their choice.
     *
     * If the data IS loaded we check whether the day is open and has availability.
     * Closed or fully-booked days return without calling onChange (return undefined).
     */
    function handleDateChange(date: Date) {
        const isDifferentMonth =
            getDayStatesMonthKey(date) !== getDayStatesMonthKey(selectedMonth ?? new Date());

        const navigateToMonth = () => {
            if (isDifferentMonth) {
                setSelectedMonth(moment(date).startOf("month").toDate());
            }
        };

        const monthData = queryClient.getQueryData<Days>(
            getDayStatesQueryKey(date, !!bookingRequestMode)
        );

        // Month data not loaded — navigate to trigger the fetch; don't select yet
        if (!monthData) {
            navigateToMonth();
            return;
        }

        const dateKey = moment(date).format("YYYY-MM-DD");
        const dayData = monthData[dateKey];

        // Determine whether the day is truly closed (the section/day is not open at all).
        // A truly closed day has nothing to offer — block navigation and reset the selection.
        //
        // A fully booked day (open = true, but no available/waitlist seats) is different: the
        // restaurant may have configured text-row messages (e.g. "Drop-in only today"). In that
        // case we allow the user through so the time-selection screen can display those messages.
        let dayIsTrulyClosed: boolean;
        if (sectionId !== undefined) {
            const sectionData = dayData?.bySection[sectionId];
            dayIsTrulyClosed = !sectionData?.open;
        } else if (experienceId !== undefined) {
            const activeSections = Object.values(dayData?.bySection ?? {}).filter(
                (section) => section.open && section.activeExperienceIds.includes(experienceId)
            );
            dayIsTrulyClosed = activeSections.length === 0;
        } else {
            dayIsTrulyClosed = !dayData?.open;
        }

        // Truly closed — reset selection and stay on the calendar
        if (dayIsTrulyClosed) {
            onChange?.(undefined);
            navigateToMonth();
            return;
        }

        // Available, waitlisted, or fully booked — navigate and select the date.
        // Fully booked days may carry text-row messages shown on the time-selection screen.
        navigateToMonth();
        onChange?.(date);
    }

    useEffect(() => {
        const setLocaleKey = (lng: string) => {
            const selectedLocaleKey = getDatePickerLocale(getLocaleFromLang(lng));
            setDatePickerLocale(selectedLocaleKey);
        };

        i18n.on("languageChanged", setLocaleKey);
        setLocaleKey(i18n.language);

        return () => {
            i18n.off("languageChanged", setLocaleKey);
        };
    }, []);

    if (dayState.isLoading) {
        return <SelectDateSkeleton />;
    }
    const data = dayState.data ?? {};
    const { closedDays, waitListDays, fullyBookedDays } =
        sectionId !== undefined
            ? getNonAvailableDatesForSection(data, sectionId, guestCount)
            : experienceId !== undefined
              ? getNonAvailableDatesForExperience(data, experienceId, guestCount)
              : getNonAvailableDatesForGuests(data, guestCount);

    // A venue can default the party size to 0 so the guest has to choose one. Until they do,
    // availability is checked for 0 guests and every open day comes back fully booked.
    const awaitingGuests = guestCount === 0;

    // Calendars that never show availability, so a prompt to choose guests would mislead.
    const showsNoAvailability = bookingRequestMode || requestOnly || informationOnly;

    // None of these have bookable seats to report, so the dots would mark every open day alike.
    const hideAvailabilityDots = showsNoAvailability || awaitingGuests;

    const nextMonthDate = moment(selectedMonth ?? new Date()).add(1, "months");
    const nextMonth = nextMonthDate.startOf("month").toDate();
    const isLastAvailableMonth = externalBookingSettings
        ? !isDateWithinBookingWindow(nextMonth, externalBookingSettings)
        : false;
    return (
        <>
            {awaitingGuests && !showsNoAvailability && (
                <Text mb={ThemeSpaceVariable.Small} color={ThemeColorVariable.OnBackgroundSubdued}>
                    {t("datepicker.label.selectGuests")}
                </Text>
            )}
            <Box>
                <DatePicker
                    key={(selectedMonth ?? new Date()).toString()}
                    selectedDate={date}
                    onDateChange={handleDateChange}
                    localeString={datePickerLocale}
                    selectedMonth={selectedMonth}
                    onMonthChange={setSelectedMonth}
                    strikeThroughDates={closedDays}
                    dots={{
                        yellowDotDates: hideAvailabilityDots ? [] : waitListDays,
                        redDotDates: hideAvailabilityDots ? [] : fullyBookedDays,
                    }}
                    shouldDisableDate={(date: Date) =>
                        externalBookingSettings
                            ? shouldDisableDate(date, externalBookingSettings)
                            : false
                    }
                    disablePastMonths
                    disableForwardNavigation={isLastAvailableMonth}
                />
            </Box>
            {!hideAvailabilityDots && (
                <DatePickerLegend fullyBookedDays={fullyBookedDays} waitListDays={waitListDays} />
            )}

            {!bookingRequestMode && firstAvailableDay && !hasAvailableDaysForMonth && (
                <FirstAvailableDay
                    date={firstAvailableDay}
                    goToNextAvailableDay={goToNextAvailableDay}
                />
            )}
        </>
    );
};
